/**
 * Das Konto-Menü an der Nutzerkarte unten in der Seitenleiste.
 * (29.09.2026, Ticket 2797)
 *
 * Martin, per Sprechtaste: Er war in einem anderen Konto und wollte sich
 * schnell abmelden, musste den Knopf aber suchen. Wunsch: wie bei Google
 * oder Claude — ein Tipp auf den Namen unten öffnet ein kleines Menü, darin
 * „Abmelden“. Erwartet: Abmelden mit zwei Tipps von überall.
 *
 * Das Menü ist ein Menü-Knopf nach WAI-ARIA: der Knopf trägt
 * `aria-haspopup="menu"` und `aria-expanded`, die Punkte sind `menuitem`.
 * Pfeiltasten wandern, Pos1/Ende springen, Escape schließt und gibt den
 * Fokus an den Knopf zurück, Tab schließt und lässt den Fokus weiterziehen.
 *
 * ⚠️ EIN Besitzer: `menue.hidden`. `aria-expanded` wird nur in `setze()`
 *    daraus abgeleitet — nie an anderer Stelle geschrieben.
 * ⚠️ Das Menü kennt KEINEN eigenen Abmelde-Weg. Was ein Punkt tut, gibt
 *    app.ts mit (`wege`): Abmelden ist dort `handleLogout()` — derselbe Weg
 *    wie in den Einstellungen, mit derselben Rückfrage.
 * ⚠️ Escape endet hier (stopPropagation): Am Telefon schließt Escape sonst
 *    auch gleich die ganze Seitenleiste (document-Hörer in app.ts). Erst
 *    das Menü, beim zweiten Escape die Leiste.
 */

/** Was die Punkte tun — Schlüssel ist `data-nutzer-aktion` im Markup. */
type NutzerMenueWege = Record<string, () => void>;

export function bindeNutzerMenue(karte: HTMLElement, wege: NutzerMenueWege): void {
  const knopf = karte.querySelector<HTMLButtonElement>(".nav-user-knopf");
  const menue = karte.querySelector<HTMLElement>(".nav-user-menue");
  if (!knopf || !menue) return;

  const punkte = (): HTMLElement[] =>
    Array.from(menue.querySelectorAll<HTMLElement>('[role="menuitem"]'));
  const offen = (): boolean => !menue.hidden;

  /*
   * Ein Tipp ausserhalb schliesst. `pointerdown` in der Fangphase, damit der
   * Hörer nicht vom selben Klick ausgelöst wird, der das Menü gerade
   * geöffnet hat (ein `click`-Hörer, der während des Klicks am document
   * angemeldet wird, bekommt diesen Klick noch mit).
   */
  const draussen = (ev: Event): void => {
    if (!karte.contains(ev.target as Node)) schliesse(false);
  };

  const setze = (auf: boolean): void => {
    menue.hidden = !auf;
    knopf.setAttribute("aria-expanded", auf ? "true" : "false");
    if (auf) document.addEventListener("pointerdown", draussen, true);
    else document.removeEventListener("pointerdown", draussen, true);
  };

  const oeffne = (wohin: "erster" | "letzter"): void => {
    setze(true);
    const p = punkte();
    (wohin === "erster" ? p[0] : p[p.length - 1])?.focus();
  };

  const schliesse = (fokusZurueck: boolean): void => {
    if (!offen()) return;
    setze(false);
    if (fokusZurueck) knopf.focus();
  };

  knopf.addEventListener("click", () => {
    if (offen()) schliesse(true);
    else oeffne("erster");
  });

  knopf.addEventListener("keydown", (ev) => {
    if (ev.key === "ArrowDown" || ev.key === "ArrowUp") {
      ev.preventDefault();
      oeffne(ev.key === "ArrowDown" ? "erster" : "letzter");
    }
  });

  karte.addEventListener("keydown", (ev) => {
    if (ev.key === "Escape" && offen()) {
      ev.preventDefault();
      ev.stopPropagation();
      schliesse(true);
    }
  });

  menue.addEventListener("keydown", (ev) => {
    const p = punkte();
    if (p.length === 0) return;
    const i = p.indexOf(document.activeElement as HTMLElement);
    let ziel: HTMLElement | undefined;
    if (ev.key === "ArrowDown") ziel = p[(i + 1) % p.length];
    else if (ev.key === "ArrowUp") ziel = p[(i - 1 + p.length) % p.length];
    else if (ev.key === "Home") ziel = p[0];
    else if (ev.key === "End") ziel = p[p.length - 1];
    else if (ev.key === "Tab") {
      schliesse(false);
      return;
    }
    if (!ziel) return;
    ev.preventDefault();
    ziel.focus();
  });

  menue.addEventListener("click", (ev) => {
    const punkt = (ev.target as Element | null)?.closest<HTMLElement>("[data-nutzer-aktion]");
    const weg = punkt ? wege[punkt.dataset.nutzerAktion ?? ""] : undefined;
    if (!weg) return;
    // Erst zu, Fokus zurück — bricht jemand die Rückfrage beim Abmelden ab,
    // steht er wieder am Knopf und nicht im Leeren.
    schliesse(true);
    weg();
  });
}
